import { HighLevelContext } from '@/types';

/**
 * Gets HighLevel context from sessionStorage (set by message listener)
 * For initial page load, context may not be available immediately
 */
export function getHighLevelContext(): HighLevelContext | null {
  try {
    // Check sessionStorage first (set by message listener after decryption)
    const storedContext = sessionStorage.getItem('highlevel_context');
    if (storedContext) {
      return JSON.parse(storedContext);
    }

    // Development fallback
    if (import.meta.env.DEV) {
      return {
        locationId: 'dev_location_id',
        authToken: 'dev_auth_token',
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to get HighLevel context:', error);
    return null;
  }
}

/**
 * Request user context from HighLevel parent window
 * HighLevel sends encrypted data that must be decrypted on the backend
 * See: https://marketplace.gohighlevel.com/docs/other/user-context-marketplace-apps
 */
export function requestHighLevelContext(): void {
  if (window.parent !== window) {
    console.log('Requesting user data from HighLevel parent window...');
    // This is the correct message format per HighLevel docs
    window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
  } else {
    console.log('Not in iframe, skipping HighLevel context request');
  }
}

/**
 * Listen for messages from parent window (HighLevel)
 * HighLevel sends encrypted user data that must be decrypted on the backend
 */
export function initHighLevelListener(
  callback: (context: HighLevelContext) => void
) {
  const handleMessage = async (event: MessageEvent) => {
    console.log('Received postMessage:', event.data);

    // Handle HighLevel's encrypted user data response
    // HighLevel sends: { message: 'REQUEST_USER_DATA_RESPONSE', payload: 'encrypted_string' }
    if (event.data?.message === 'REQUEST_USER_DATA_RESPONSE' && event.data?.payload) {
      const encryptedData = event.data.payload;
      console.log('Received REQUEST_USER_DATA_RESPONSE from HighLevel, sending to backend for decryption...');
      console.log('Encrypted payload length:', encryptedData.length);

      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://highlevel-xero-api.fly.dev';
        const response = await fetch(`${apiUrl}/auth/decrypt-context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ encryptedData }),
        });

        if (response.ok) {
          const context = await response.json();
          console.log('Decrypted HighLevel context:', context);
          sessionStorage.setItem('highlevel_context', JSON.stringify(context));
          callback(context);
        } else {
          const errorText = await response.text();
          console.error('Failed to decrypt HighLevel context:', errorText);
        }
      } catch (error) {
        console.error('Error decrypting HighLevel context:', error);
      }
      return; // Don't process other handlers
    }

    // Also handle our own format for backwards compatibility
    if (event.data?.type === 'HIGHLEVEL_CONTEXT') {
      const context = event.data.context as HighLevelContext;
      sessionStorage.setItem('highlevel_context', JSON.stringify(context));
      callback(context);
    }
  };

  window.addEventListener('message', handleMessage);

  // Request context from parent immediately
  requestHighLevelContext();

  return () => {
    window.removeEventListener('message', handleMessage);
  };
}
